// Reservations module — Dispenser cubby reservations (window.Reservations).
// Creates/releases reservations against the cubby-availability API. Runtime dependencies: PickupContext, KROWN.

// Reserve failure codes returned by POST /reserve. The server mirrors these exact values.
const RESERVE_ERROR_CODES = {
  EXCEEDS_MAX_BOXES: 'EXCEEDS_MAX_BOXES',
  INSUFFICIENT_AVAILABILITY: 'INSUFFICIENT_AVAILABILITY',
  CART_EMPTY: 'CART_EMPTY',
  INVALID_REQUEST: 'INVALID_REQUEST',
  INVALID_PICKUP_LOCATION: 'INVALID_PICKUP_LOCATION',
  INVALID_PICKUP_DATE: 'INVALID_PICKUP_DATE',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
};

/**
 * Set or clear the reservation cart attributes
 * `_cubby_reservation_id` points at one DynamoDB reservation row; its server-side `quantity`
 * is how many cubbies the cart holds (1–4). Used to dedupe a completed order against that row.
 *
 * `_gk_cancel_token` is the signed token that lets a guest cancel this order later. It is written
 * here, during checkout, because Shopify sends the order confirmation email as part of creating the
 * order — anything the email must contain has to already be on the cart. The Order confirmation
 * notification template reads it back as `{{ attributes._gk_cancel_token }}` to build the cancel
 * link. It is not treated as required: an older server that does not return one still reserves fine,
 * and signed-in customers never need it.
 *
 * @returns {Promise<boolean>}
 */
async function setReservationCartAttributes(expiresAtIso, reservationId, cancelToken) {
  // The form is just a container for FormData (never submitted/attached), so no document insertion is needed.
  const form = document.createElement('form');

  const addAttribute = (name, value) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = `attributes[${name}]`;
    input.value = value || '';
    form.appendChild(input);
  };

  addAttribute('reservation_expires_at', expiresAtIso);
  addAttribute('_cubby_reservation_id', reservationId);
  addAttribute('_gk_cancel_token', cancelToken);

  try {
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      body: new FormData(form),
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    });
    if (!response.ok) return false;
    const cart = await response.json();
    // The cancel token is not verified here: losing it costs a guest their cancel link, which is not
    // worth failing (and rolling back) an otherwise good reservation over.
    return (
      cart.attributes?.reservation_expires_at === (expiresAtIso || '') &&
      cart.attributes?._cubby_reservation_id === (reservationId || '')
    );
  } catch (error) {
    console.error('Error updating reservation cart attributes:', error);
    return false;
  }
}

/**
 * Create a cubbie reservation for the current cart and write its expiration time to the cart attribute.
 * @returns {Promise<object|null>} null when reservations are disabled; otherwise { success, code? } with the reservation on success
 */
async function createImmediateReservation(locationHandle, pickupDate) {
  if (!window.CUBBIE_RESERVATIONS_ENABLED) return null;
  try {
    const cartResponse = await fetch('/cart.js');
    const cart = await cartResponse.json();

    if (cart.item_count === 0) {
      return { success: false, code: RESERVE_ERROR_CODES.CART_EMPTY };
    }

    const reservation = await reserveCubbies(locationHandle, pickupDate, cart);

    if (!reservation.success) return reservation;

    const attributeSaved = await setReservationCartAttributes(
      reservation.expiresAt,
      reservation.reservationId,
      reservation.cancelToken
    );
    if (!attributeSaved) {
      await releaseReservation(cart.token);
      return { success: false, code: RESERVE_ERROR_CODES.INTERNAL_ERROR };
    }

    return {
      success: true,
      reservationId: reservation.reservationId,
      expiresAt: reservation.expiresAt,
      locationHandle,
      date: pickupDate,
    };
  } catch (error) {
    console.error('Error creating immediate reservation:', error);
    return { success: false, code: RESERVE_ERROR_CODES.INTERNAL_ERROR };
  }
}

if (window.CUBBIE_RESERVATIONS_ENABLED) {
  const disclaimer = document.getElementById('pickup-reservation-disclaimer');
  if (disclaimer) disclaimer.style.display = '';
}

// Reserve cubbies for the cart via the cubbie-availability API. The server derives the boxes needed
// from the cart and returns a machine-readable code on failure (see RESERVE_ERROR_CODES).
async function reserveCubbies(locationHandle, date, cart) {
  if (!window.CUBBIE_RESERVATIONS_ENABLED) return { success: false, code: RESERVE_ERROR_CODES.INTERNAL_ERROR };
  try {
    const apiUrl = KROWN.settings.api_urls.cubbie_availability_app_url;
    if (!apiUrl) {
      console.warn('Cubbie availability API URL not configured');
      return { success: false, code: RESERVE_ERROR_CODES.INTERNAL_ERROR };
    }

    const requestData = {
      locationHandle,
      date,
      cart,
    };

    const response = await fetch(`${apiUrl}/reserve`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestData),
    });

    const result = await response.json().catch(() => ({}));

    if (!response.ok) {
      return { success: false, code: result.code || RESERVE_ERROR_CODES.INTERNAL_ERROR, message: result.message };
    }

    return {
      success: true,
      reservationId: result.reservationId,
      expiresAt: result.expiresAt,
      cancelToken: result.cancelToken,
    };
  } catch (error) {
    console.error('Error reserving cubbies:', error);
    return { success: false, code: RESERVE_ERROR_CODES.INTERNAL_ERROR };
  }
}

// Release any reservations held for the cart via the cubbie-availability API.
async function releaseReservation(cartId) {
  try {
    const apiUrl = KROWN.settings.api_urls.cubbie_availability_app_url;
    if (!apiUrl) {
      console.warn('Cubbie availability API URL not configured');
      return null;
    }

    const response = await fetch(`${apiUrl}/release`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ cartId }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `API responded with status ${response.status}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error releasing reservation:', error);
    throw error;
  }
}

// Public namespace for cross-file callers; internal calls use the bare function names.
window.Reservations = {
  createImmediateReservation,
  RESERVE_ERROR_CODES,
};
